import { useEffect } from 'react';
import { formatUnits } from 'viem';
import { useAccount, useReadContract, useWatchContractEvent } from 'wagmi';
import type { MarketPair } from '../data/mockMarket';
import { PAIR_ABI } from './abis';
import { TARGET_CHAIN } from './config';
import { getPairAddress, getTokenAddress } from './contracts';
import { recordPriceSample, recordVolumeSample, readMarketStats } from './priceHistory';

const DECIMALS = 18;

// price of BNB in USDT, read from the real BNB-USDT pool reserves. React Query
// dedupes identical reads, so calling this from every pool's hook costs nothing extra.
function useBnbUsdPrice(chainId: number) {
  const pairAddress = getPairAddress('BNB-USDT', chainId);
  const bnbAddress = getTokenAddress('BNB', chainId);

  const { data: reserves } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'getReserves',
    chainId,
    query: { enabled: Boolean(pairAddress), refetchInterval: 8000 },
  });
  const { data: token0 } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'token0',
    chainId,
    query: { enabled: Boolean(pairAddress) },
  });

  if (!reserves || !token0 || !bnbAddress) return undefined;
  const [reserve0, reserve1] = reserves;
  const bnbIsToken0 = token0.toLowerCase() === bnbAddress.toLowerCase();
  const reserveBnb = bnbIsToken0 ? reserve0 : reserve1;
  const reserveUsdt = bnbIsToken0 ? reserve1 : reserve0;
  if (reserveBnb === 0n) return undefined;
  return Number(formatUnits(reserveUsdt, DECIMALS)) / Number(formatUnits(reserveBnb, DECIMALS));
}

export function useLiveMarket(pool: MarketPair) {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const pairAddress = getPairAddress(pool.id, readChainId);
  const baseAddress = getTokenAddress(pool.base, readChainId);

  const { data: reserves } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'getReserves',
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress), refetchInterval: 8000 },
  });
  const { data: token0 } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'token0',
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress) },
  });

  const bnbUsdPrice = useBnbUsdPrice(readChainId);
  // USDT/BUSD are both ~$1 stable test tokens; BNB needs the live cross rate above
  const quoteUsdRate = pool.quote === 'BNB' ? bnbUsdPrice : 1;

  let price: number | undefined;
  let reserveBaseHuman: number | undefined;
  let reserveQuoteHuman: number | undefined;
  let baseIsToken0 = true;

  if (reserves && token0 && baseAddress) {
    const [reserve0, reserve1] = reserves;
    baseIsToken0 = token0.toLowerCase() === baseAddress.toLowerCase();
    const reserveBase = baseIsToken0 ? reserve0 : reserve1;
    const reserveQuote = baseIsToken0 ? reserve1 : reserve0;
    reserveBaseHuman = Number(formatUnits(reserveBase, DECIMALS));
    reserveQuoteHuman = Number(formatUnits(reserveQuote, DECIMALS));
    if (reserveBaseHuman > 0) price = reserveQuoteHuman / reserveBaseHuman;
  }

  useEffect(() => {
    if (price !== undefined) recordPriceSample(pool.id, price);
  }, [pool.id, price]);

  useWatchContractEvent({
    address: pairAddress,
    abi: PAIR_ABI,
    eventName: 'Swap',
    chainId: readChainId,
    enabled: Boolean(pairAddress && quoteUsdRate !== undefined),
    onLogs: (logs) => {
      if (quoteUsdRate === undefined) return;
      for (const log of logs) {
        const { amount0In, amount1In, amount0Out, amount1Out } = log.args;
        if (amount0In === undefined) continue;
        const quoteAmountRaw = baseIsToken0 ? (amount1In ?? 0n) + (amount1Out ?? 0n) : (amount0In ?? 0n) + (amount0Out ?? 0n);
        const quoteAmountHuman = Number(formatUnits(quoteAmountRaw, DECIMALS));
        recordVolumeSample(pool.id, quoteAmountHuman * quoteUsdRate);
      }
    },
  });

  const stats = readMarketStats(pool.id, price);
  const liquidityUsd =
    reserveQuoteHuman !== undefined && quoteUsdRate !== undefined ? 2 * reserveQuoteHuman * quoteUsdRate : undefined;

  return {
    price,
    change24h: stats.change24h,
    high24h: stats.high24h,
    low24h: stats.low24h,
    volume24h: stats.volume24h,
    liquidityUsd,
    series: stats.series,
  };
}
